<template>
  <v-card hover @click="handleCardClick" style="cursor: pointer; display: flex; flex-direction: column; height: 100%;">
    <v-card-title style="font-size: 16px; font-weight: bold; word-break: break-word;">
      <i class="fad fa-lightbulb-on" style="margin-right: 10px; color: #FFB024;"></i>
      {{ $t("fitness.kpi_card_title") || "Fitness Insights" }} 
      <span v-if="dateRange" style="font-size: 14px; margin-left: 8px; font-weight: normal; color: grey;">({{ dateRange }})</span>
    </v-card-title>
    
    <div v-if="loading" style="text-align: center; height: 100px; display: flex; align-items: center; justify-content: center;">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <div v-else-if="randomInsight" style="padding: 0 16px 16px; flex-grow: 1; display: flex; flex-direction: column; justify-content: center;">
      <div style="font-size: 18px; font-weight: bold; margin-bottom: 5px;">
        <i :class="'fad fa-' + randomInsight.icon" style="margin-right: 5px; color: #FFB024;"></i>
        {{ randomInsight.title }}
      </div>
      <div style="font-size: 14px;" v-html="randomInsight.text"></div>
    </div>
    <div v-else style="padding: 10px 20px 20px; text-align: center; color: grey;">
      {{ $t("fitness.no_data_here") }}
    </div>
  </v-card>
</template>

<script>
import { storage } from "../../schmucklicloud";
var numeral = require("numeral");

export default {
  props: {
    date: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      insights: [],
      randomInsight: null
    };
  },
  watch: {
    date() {
      this.loadData();
    }
  },
  computed: {
    dateRange() {
        if (!this.date) return '';
        let parts = this.date.split('-');
        if (parts.length >= 2) {
            let year = parseInt(parts[0]);
            let month = parseInt(parts[1]);
            let lastDay = new Date(year, month, 0).getDate();
            let mStr = month.toString().padStart(2, '0');
            return `01.${mStr}.${year} - ${lastDay}.${mStr}.${year}`;
        }
        return '';
    }
  },
  mounted() {
    this.loadData();
  },
  methods: {
    handleCardClick(event) {
        // Only navigate if the click wasn't on a link inside the text
        if (event.target.tagName.toLowerCase() !== 'a') {
            this.$router.push('/fitness_stats');
        }
    },
    async loadData() {
      this.loading = true;
      var response = await storage.get("fitness_daily", [
        {
          column: "date",
          operator: "contains",
          value: this.date ? this.date.substring(0, 7) : "-",
        },
      ]);

      this.insights = [];
      this.randomInsight = null;

      if (response.isOK && response.data) {
        this.generateInsights(response.data);
        if (this.insights.length > 0) {
           this.randomInsight = this.insights[Math.floor(Math.random() * this.insights.length)];
        }
      }
      this.loading = false;
    },
    getHoursAndMinutesFromMinutes(input_minutes) {
      var minutes = parseInt(input_minutes);
      var hours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      return hours + "h " + minutes + "m";
    },
    formatDate(dateStr) {
        if (!dateStr) return '';
        var months = [
            this.$t("general.month.january"),
            this.$t("general.month.february"),
            this.$t("general.month.march"),
            this.$t("general.month.april"),
            this.$t("general.month.may"),
            this.$t("general.month.june"),
            this.$t("general.month.july"),
            this.$t("general.month.august"),
            this.$t("general.month.september"),
            this.$t("general.month.october"),
            this.$t("general.month.november"),
            this.$t("general.month.december"),
        ];

        let parts = dateStr.split('-');
        if (parts.length === 3) {
            let monthName = months[parseInt(parts[1]) - 1];
            return `${parseInt(parts[2])}. ${monthName} ${parts[0]}`;
        } else if (parts.length === 2) {
            let monthName = months[parseInt(parts[1]) - 1];
            return `${monthName} ${parts[0]}`;
        }
        return dateStr;
    },
    formatDateLink(dateStr) {
        if (!dateStr) return '';
        let formatted = this.formatDate(dateStr);
        return `<a href="#/dashboard/${dateStr}" style="text-decoration: underline; color: inherit; font-weight: bold;">${formatted}</a>`;
    },
    generateInsights(data) {
        this.insights = [];
        if (!data || data.length === 0) return;

        let maxStepsDay = null;
        let maxSteps = 0;
        
        let totalDistance = 0;

        let maxSleepDay = null;
        let maxSleep = 0;

        let maxFloorsDay = null;
        let maxFloors = 0;

        let currentStreak = 0;
        let maxStreak = 0;
        
        let maxCaloriesDay = null;
        let maxCalories = 0;

        let totalSteps = 0;
        let minStepsDay = null;
        let minSteps = Infinity;
        let daysOver7HoursSleep = 0;
        let daysWithSteps = 0;
        
        let maxWeekendDay = null;
        let maxWeekendSteps = 0;

        let minHrDay = null;
        let minHr = Infinity;
        
        let maxDistanceDay = null;
        let maxDistance = 0;

        let minSleepDay = null;
        let minSleep = Infinity;

        let goalDays = 0;

        let firstWeight = null;
        let lastWeight = null;
        
        let minBodyFatDay = null;
        let minBodyFat = Infinity;

        let maxMuscleDay = null;
        let maxMuscle = 0;

        let sortedData = [...data].sort((a, b) => a.data.date.localeCompare(b.data.date));

        sortedData.forEach(item => {
            let d = item.data;
            let s = parseInt(d.steps) || 0;
            let dist = parseFloat(d.distance) || 0;
            let sleep = parseInt(d.sleep_duration) || 0;
            let floors = parseInt(d.floors) || 0;
            let cals = parseInt(d.calories) || 0;

            totalDistance += dist;
            totalSteps += s;

            if (s > 0) {
                daysWithSteps++;
                if (s < minSteps) {
                    minSteps = s; minStepsDay = d.date;
                }
            }

            if (sleep >= 420) {
                daysOver7HoursSleep++;
            }

            if (s > maxSteps) { maxSteps = s; maxStepsDay = d.date; }
            if (floors > maxFloors) { maxFloors = floors; maxFloorsDay = d.date; }
            if (cals > maxCalories) { maxCalories = cals; maxCaloriesDay = d.date; }
            
            if (sleep > 0) {
                if (sleep > maxSleep) { maxSleep = sleep; maxSleepDay = d.date; }
            }
            
            if (s >= 10000) {
                currentStreak++;
                if (currentStreak > maxStreak) {
                    maxStreak = currentStreak;
                }
            } else {
                currentStreak = 0;
            }
            
            let hr = parseInt(d.heart_rate) || 0;
            if (hr > 0 && hr < minHr) { minHr = hr; minHrDay = d.date; }

            let dateObj = new Date(d.date);
            if (dateObj.getDay() === 0 || dateObj.getDay() === 6) { 
                if (s > maxWeekendSteps) { maxWeekendSteps = s; maxWeekendDay = d.date; }
            }
            
            if (dist > maxDistance) { maxDistance = dist; maxDistanceDay = d.date; }
            if (sleep > 0 && sleep < minSleep) { minSleep = sleep; minSleepDay = d.date; }
            if (s >= 10000) { goalDays++; }
            
            let w = parseFloat(d.weight) || 0;
            let bf = parseFloat(d.body_fat) || 0;
            let mm = parseFloat(d.muscle_mass) || 0;

            if (w > 0) {
                if (firstWeight === null) firstWeight = w;
                lastWeight = w;
            }

            if (bf > 0 && bf < minBodyFat) {
                minBodyFat = bf;
                minBodyFatDay = d.date;
            }

            if (mm > maxMuscle) {
                maxMuscle = mm;
                maxMuscleDay = d.date;
            }
        });

        if (totalSteps > 0) {
            this.insights.push({
                icon: 'walking',
                title: this.$t("fitness.insight_total_steps_title"),
                text: this.$t("fitness.insight_total_steps_text", {
                    steps: numeral(totalSteps).format("0,000").replace(/,/g, "'")
                })
            });

            let avgSteps = Math.round(totalSteps / daysWithSteps);
            this.insights.push({
                icon: 'chart-line',
                title: this.$t("fitness.insight_avg_steps_title"),
                text: this.$t("fitness.insight_avg_steps_text", {
                    steps: numeral(avgSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (maxStepsDay && maxSteps > 0) {
            this.insights.push({
                icon: 'shoe-prints',
                title: this.$t("fitness.insight_most_active_day_title"),
                text: this.$t("fitness.insight_most_active_day_text", {
                    date: this.formatDateLink(maxStepsDay),
                    steps: numeral(maxSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (totalDistance > 0) {
            let marathons = (totalDistance / 42.195).toFixed(1);
            this.insights.push({
                icon: 'map',
                title: this.$t("fitness.insight_distance_milestone_title"),
                text: this.$t("fitness.insight_distance_milestone_text", {
                    distance: numeral(totalDistance).format("0,000.0").replace(/,/g, "'"),
                    marathons: marathons
                })
            });
        }

        if (maxSleepDay && maxSleep > 0) {
            let sleepText = this.getHoursAndMinutesFromMinutes(maxSleep);
            this.insights.push({
                icon: 'moon',
                title: this.$t("fitness.insight_best_sleep_title"),
                text: this.$t("fitness.insight_best_sleep_text", {
                    date: this.formatDateLink(maxSleepDay),
                    sleep: sleepText
                })
            });
        }

        if (maxFloorsDay && maxFloors > 0) {
            this.insights.push({
                icon: 'level-up',
                title: this.$t("fitness.insight_peak_climbing_title"),
                text: this.$t("fitness.insight_peak_climbing_text", {
                    date: this.formatDateLink(maxFloorsDay),
                    floors: numeral(maxFloors).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (maxStreak > 1) {
            this.insights.push({
                icon: 'fire-alt',
                title: this.$t("fitness.insight_active_streak_title"),
                text: this.$t("fitness.insight_active_streak_text", {
                    streak: maxStreak
                })
            });
        }
        
        if (maxCaloriesDay && maxCalories > 0) {
            this.insights.push({
                icon: 'flame',
                title: this.$t("fitness.insight_calorie_burner_title"),
                text: this.$t("fitness.insight_calorie_burner_text", {
                    date: this.formatDateLink(maxCaloriesDay),
                    calories: numeral(maxCalories).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (minStepsDay && minSteps !== Infinity) {
            this.insights.push({
                icon: 'battery-quarter',
                title: this.$t("fitness.insight_rest_day_title"),
                text: this.$t("fitness.insight_rest_day_text", {
                    date: this.formatDateLink(minStepsDay),
                    steps: numeral(minSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (daysOver7HoursSleep > 0) {
            this.insights.push({
                icon: 'bed',
                title: this.$t("fitness.insight_sleep_consistency_title"),
                text: this.$t("fitness.insight_sleep_consistency_text", {
                    days: daysOver7HoursSleep
                })
            });
        }
        
        if (maxWeekendDay && maxWeekendSteps > 0) {
            this.insights.push({
                icon: 'calendar-star',
                title: this.$t("fitness.insight_weekend_warrior_title"),
                text: this.$t("fitness.insight_weekend_warrior_text", {
                    date: this.formatDateLink(maxWeekendDay),
                    steps: numeral(maxWeekendSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }

        if (minHrDay && minHr !== Infinity && minHr > 30) {
            this.insights.push({
                icon: 'heartbeat',
                title: this.$t("fitness.insight_lowest_hr_title"),
                text: this.$t("fitness.insight_lowest_hr_text", {
                    date: this.formatDateLink(minHrDay),
                    hr: minHr
                })
            });
        }
        
        let bounceBackSteps = 0;
        let bounceBackDate = null;
        let restDate = null;
        if (minStepsDay && minSteps !== Infinity) {
            let minDateObj = new Date(minStepsDay);
            minDateObj.setDate(minDateObj.getDate() + 1);
            let m = (minDateObj.getMonth() + 1).toString().padStart(2, '0');
            let day = minDateObj.getDate().toString().padStart(2, '0');
            let nextDayStr = `${minDateObj.getFullYear()}-${m}-${day}`;
            let nextDayItem = sortedData.find(item => item.data.date === nextDayStr);
            if (nextDayItem) {
                bounceBackSteps = parseInt(nextDayItem.data.steps) || 0;
                if (bounceBackSteps > minSteps * 2 && bounceBackSteps > 5000) {
                    bounceBackDate = nextDayStr;
                    restDate = minStepsDay;
                }
            }
        }

        if (bounceBackDate && bounceBackSteps > 0) {
            this.insights.push({
                icon: 'history',
                title: this.$t("fitness.insight_bounce_back_title"),
                text: this.$t("fitness.insight_bounce_back_text", {
                    restDate: this.formatDateLink(restDate),
                    date: this.formatDateLink(bounceBackDate),
                    steps: numeral(bounceBackSteps).format("0,000").replace(/,/g, "'")
                })
            });
        }
        
        if (maxDistanceDay && maxDistance > 0) {
            this.insights.push({
                icon: 'route',
                title: this.$t("fitness.insight_max_distance_title"),
                text: this.$t("fitness.insight_max_distance_text", {
                    date: this.formatDateLink(maxDistanceDay),
                    distance: numeral(maxDistance).format("0,000.0").replace(/,/g, "'")
                })
            });
        }

        if (minSleepDay && minSleep !== Infinity) {
            let sleepText = this.getHoursAndMinutesFromMinutes(minSleep);
            this.insights.push({
                icon: 'coffee',
                title: this.$t("fitness.insight_min_sleep_title"),
                text: this.$t("fitness.insight_min_sleep_text", {
                    date: this.formatDateLink(minSleepDay),
                    sleep: sleepText
                })
            });
        }

        if (goalDays > 0) {
            this.insights.push({
                icon: 'bullseye-arrow',
                title: this.$t("fitness.insight_goal_crusher_title"),
                text: this.$t("fitness.insight_goal_crusher_text", {
                    days: goalDays
                })
            });
        }
        
        if (firstWeight !== null && lastWeight !== null && firstWeight !== lastWeight) {
            let diff = parseFloat(Math.abs(firstWeight - lastWeight).toFixed(2));
            if (diff > 0) {
                if (firstWeight > lastWeight) {
                    this.insights.push({
                        icon: 'weight',
                        title: this.$t("fitness.insight_weight_journey_title"),
                        text: this.$t("fitness.insight_weight_journey_lost_text", {
                            diff: numeral(diff).format("0.00").replace(/,/g, "'")
                        })
                    });
                } else {
                    this.insights.push({
                        icon: 'weight',
                        title: this.$t("fitness.insight_weight_journey_title"),
                        text: this.$t("fitness.insight_weight_journey_gained_text", {
                            diff: numeral(diff).format("0.00").replace(/,/g, "'")
                        })
                    });
                }
            }
        }

        if (minBodyFatDay && minBodyFat !== Infinity) {
            this.insights.push({
                icon: 'percentage',
                title: this.$t("fitness.insight_lowest_body_fat_title"),
                text: this.$t("fitness.insight_lowest_body_fat_text", {
                    date: this.formatDateLink(minBodyFatDay),
                    body_fat: numeral(minBodyFat).format("0.00").replace(/,/g, "'")
                })
            });
        }

        if (maxMuscleDay && maxMuscle > 0) {
            this.insights.push({
                icon: 'dumbbell',
                title: this.$t("fitness.insight_peak_muscle_mass_title"),
                text: this.$t("fitness.insight_peak_muscle_mass_text", {
                    date: this.formatDateLink(maxMuscleDay),
                    muscle: numeral(maxMuscle).format("0.00").replace(/,/g, "'")
                })
            });
        }
    }
  }
};
</script>

<style scoped>
</style>
